import React, { useState } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button,
  Typography,
  Box,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Paper,
  Divider
} from '@mui/material';
import { 
  CheckCircle as CheckCircleIcon,
  Security as SecurityIcon,
  Fingerprint as FingerprintIcon
} from '@mui/icons-material';
import { useAlerts } from '../../contexts/AlertsContext';
import FingerprintAuth from '../auth/FingerprintAuth';

const ResolveAlertDialog = ({ open, onClose, alert }) => {
  const { resolveAlert } = useAlerts();
  
  const [resolutionReason, setResolutionReason] = useState('');
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [fingerprintDialogOpen, setFingerprintDialogOpen] = useState(false);
  const [isResolved, setIsResolved] = useState(false);
  
  const resolutionReasons = [
    { value: 'mistake', label: 'False Alarm/Mistake', description: 'The alert was triggered by mistake or was a false alarm' },
    { value: 'recovered', label: 'Item Recovered', description: 'The asset has been recovered and is now secure' },
    { value: 'found', label: 'Item Found', description: 'The asset was found and was not actually stolen/missing' },
    { value: 'other', label: 'Other', description: 'Other reason (please specify in notes)' }
  ];
  
  const handleSubmit = () => {
    setFingerprintDialogOpen(true);
  };
  
  const handleFingerprintSuccess = () => {
    setFingerprintDialogOpen(false);
    
    // Create resolution data
    const resolutionData = {
      reason: resolutionReason,
      notes: additionalNotes,
      timestamp: new Date().toISOString()
    };
    
    // Resolve the alert
    resolveAlert(alert.id, JSON.stringify(resolutionData));
    
    // Show success state
    setIsResolved(true);
  };
  
  const handleClose = () => {
    if (isResolved) {
      // Reset form if resolved
      setResolutionReason('');
      setAdditionalNotes('');
      setIsResolved(false);
    }
    onClose();
  };
  
  if (!alert) return null;
  
  return (
    <>
      <Dialog 
        open={open} 
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center' }}>
          <CheckCircleIcon sx={{ mr: 1 }} />
          Resolve Alert
        </DialogTitle>
        
        <DialogContent sx={{ pt: 3, pb: 1 }}>
          {!isResolved ? (
            <Box>
              <Paper 
                variant="outlined" 
                sx={{ 
                  p: 3, 
                  mb: 3, 
                  borderRadius: 2,
                  bgcolor: alert.severity === 'critical' ? 'error.50' : 'warning.50',
                  borderColor: alert.severity === 'critical' ? 'error.200' : 'warning.200'
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Alert Details
                </Typography>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Type:
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {alert.type.replace(/_/g, ' ')}
                  </Typography>
                </Box>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Asset:
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {alert.assetName}
                  </Typography>
                </Box>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Message:
                  </Typography>
                  <Typography variant="body1">
                    {alert.message}
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Time:
                  </Typography>
                  <Typography variant="body1">
                    {new Date(alert.timestamp).toLocaleString()}
                  </Typography>
                </Box>
              </Paper>
              
              <Typography variant="h6" sx={{ mb: 2 }}>
                Resolution Information
              </Typography>
              
              <FormControl component="fieldset" sx={{ width: '100%', mb: 3 }}>
                <FormLabel component="legend" sx={{ mb: 1 }}>
                  Reason for Resolution
                </FormLabel>
                <RadioGroup
                  value={resolutionReason}
                  onChange={(e) => setResolutionReason(e.target.value)}
                >
                  {resolutionReasons.map((reason) => (
                    <Paper 
                      key={reason.value}
                      elevation={resolutionReason === reason.value ? 3 : 1}
                      sx={{ 
                        mb: 2,
                        p: 2,
                        border: resolutionReason === reason.value ? '2px solid' : '1px solid',
                        borderColor: resolutionReason === reason.value ? 'primary.main' : 'divider',
                        borderRadius: 2,
                        transition: 'all 0.2s'
                      }}
                    >
                      <FormControlLabel 
                        value={reason.value} 
                        control={<Radio color="primary" />} 
                        label={
                          <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {reason.label}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {reason.description}
                            </Typography>
                          </Box>
                        }
                        sx={{ width: '100%', m: 0 }}
                      />
                    </Paper>
                  ))}
                </RadioGroup>
              </FormControl>
              
              <TextField
                label="Additional Notes"
                multiline
                rows={4}
                fullWidth
                value={additionalNotes}
                onChange={(e) => setAdditionalNotes(e.target.value)}
                placeholder="Please provide any additional details about the resolution"
                sx={{ mb: 2 }}
              />
              
              <Box sx={{ bgcolor: 'info.50', p: 2, borderRadius: 2 }}>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                  <SecurityIcon sx={{ mr: 1, color: 'info.main' }} />
                  <span>
                    <strong>Important:</strong> Resolving this alert will mark it as completed and remove it from active alerts. Fingerprint authentication is required to proceed.
                  </span>
                </Typography>
              </Box>
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Box 
                sx={{ 
                  width: 100, 
                  height: 100, 
                  borderRadius: '50%', 
                  bgcolor: 'success.50',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2
                }}
              >
                <CheckCircleIcon sx={{ fontSize: 60, color: 'success.main' }} />
              </Box>
              
              <Typography variant="h5" sx={{ color: 'success.main', fontWeight: 600, mb: 1 }}>
                Alert Resolved Successfully
              </Typography>
              
              <Typography variant="body1" sx={{ mb: 3 }}>
                The alert has been marked as resolved and removed from active alerts.
              </Typography>
              
              <Paper variant="outlined" sx={{ p: 2, maxWidth: 400, mx: 'auto', mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Resolution Reason:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {resolutionReasons.find(r => r.value === resolutionReason)?.label || 'Other'}
                </Typography>
                
                {additionalNotes && (
                  <>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2" color="text.secondary">
                      Notes:
                    </Typography>
                    <Typography variant="body2">
                      {additionalNotes}
                    </Typography>
                  </>
                )}
              </Paper>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 3 }}>
          {!isResolved ? (
            <>
              <Button onClick={handleClose} color="inherit">
                Cancel
              </Button>
              <Button 
                onClick={handleSubmit} 
                variant="contained" 
                color="primary"
                startIcon={<FingerprintIcon />}
                disabled={!resolutionReason}
              >
                Authenticate & Resolve
              </Button>
            </>
          ) : (
            <Button onClick={handleClose} variant="contained" color="primary" fullWidth>
              Close
            </Button>
          )}
        </DialogActions>
      </Dialog>
      
      <FingerprintAuth 
        open={fingerprintDialogOpen}
        onSuccess={handleFingerprintSuccess}
        onCancel={() => setFingerprintDialogOpen(false)}
        title="Authenticate to Resolve Alert"
      />
    </>
  );
};

export default ResolveAlertDialog;
